<script setup lang="ts">
/**
 * Shared "give negative feedback" dialog. Used by both the matter-context
 * summary thumbs-down and the per-response feedback on agent chat answers.
 * Emits the selected issue type + free-text details to the parent, which
 * posts them to POST /Feedback via the relevant store.
 */
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Both callers (chat-response thumbs-down and matter-context summary thumbs-down)
// close the dialog immediately on submit and surface the in-flight request as a
// spinner on their own thumb button, so the dialog itself has no busy state.
const props = defineProps<{
    visible: boolean;
}>();

const emit = defineEmits<{
    submit: [payload: { issueType: string; text: string }];
    skip: [];
}>();

// `value` is the backend enum (sent as issueReported) and must NOT be
// translated; `labelKey` is the visible, translatable label.
const ISSUE_TYPES = [
    { value: 'Missing important information', labelKey: 'feedback.issueTypes.missingInfo' },
    { value: 'Wrong or made-up information', labelKey: 'feedback.issueTypes.wrongInfo' },
    { value: 'Wrong entities, dates, or numbers', labelKey: 'feedback.issueTypes.wrongEntities' },
    { value: 'Hard to follow or poorly written', labelKey: 'feedback.issueTypes.hardToFollow' },
    { value: 'Wrong focus or emphasis', labelKey: 'feedback.issueTypes.wrongFocus' },
] as const;

const draft = ref('');
const issueType = ref('');

watch(
    () => props.visible,
    (open) => {
        if (open) {
            draft.value = '';
            issueType.value = '';
        }
    },
);

const onSubmit = () => {
    emit('submit', { issueType: issueType.value, text: draft.value });
};

const onSkip = () => {
    emit('skip');
};
</script>

<template>
    <div v-if="visible" class="feedback-dialog-overlay" @click.self="onSkip">
        <div class="feedback-dialog" role="dialog" aria-modal="true">
            <h3 class="feedback-dialog-title">{{ t('feedback.title') }}</h3>
            <p class="feedback-dialog-body">
                {{ t('feedback.body') }}
            </p>
            <label class="feedback-dialog-label" for="feedback-issue-type">
                {{ t('feedback.issueTypeLabel') }}
            </label>
            <select
                id="feedback-issue-type"
                v-model="issueType"
                class="feedback-dialog-select"
            >
                <option value="">{{ t('feedback.selectPlaceholder') }}</option>
                <option v-for="type in ISSUE_TYPES" :key="type.value" :value="type.value">
                    {{ t(type.labelKey) }}
                </option>
            </select>
            <label class="feedback-dialog-label" for="feedback-details">
                {{ t('feedback.detailsLabel') }}
            </label>
            <textarea
                id="feedback-details"
                v-model="draft"
                class="feedback-dialog-textarea"
                :placeholder="t('feedback.detailsPlaceholder')"
                rows="6"
            ></textarea>
            <div class="feedback-dialog-actions">
                <button type="button" class="feedback-dialog-skip" @click="onSkip">
                    {{ t('common.cancel') }}
                </button>
                <button type="button" class="feedback-dialog-submit" @click="onSubmit">
                    {{ t('common.submit') }}
                </button>
            </div>
        </div>
    </div>
</template>
